import type { DemoDeLaEmpresa, FiltroDeDemo, VencimientoDeLaPrueba } from '../types/admin.types';

/*
  Reglas de la demo (343, tanda 4): los textos del panel de plataforma sobre las empresas en prueba, en
  UN solo lugar (la lista, la ficha y el diálogo los leen de acá). La pantalla no deduce ningún estado:
  recibe `demo` del servidor (`estadoDeLaDemo`) y sólo lo cuenta con palabras. Tono del panel: voseo.
*/

export type VarianteDePastilla = 'success' | 'warning' | 'info' | 'secondary' | 'destructive';

export interface PastillaDeLaDemo {
  clave:
    | 'vencida'
    | 'por-vencer'
    | 'lista'
    | 'misiones'
    | 'en-el-tope'
    | 'sin-ingresos'
    | 'rubro-sin-tiendas';
  texto: string;
  variante: VarianteDePastilla;
  /** Qué quiere decir, para el tooltip y para quien usa lector de pantalla. */
  ayuda: string;
}

export const TEXTO_LISTA_PARA_LA_VENTA = 'Lista para la venta';
export const TEXTO_RUBRO_SIN_TIENDAS = 'Rubro sin tiendas';
export const TEXTO_EN_EL_TOPE = 'En el tope';

export const textoDeMisiones = (hechas: number, total: number) => `Misiones ${hechas}/${total}`;

export const textoSinIngresos = (dias: number) =>
  `Sin ingresos hace ${dias} ${dias === 1 ? 'día' : 'días'}`;

/** Demo que vence (2-nov): el plazo de la prueba en palabras («vence en 2 días», «venció hace 3 días»). */
export function textoDelPlazo(v: VencimientoDeLaPrueba): string | null {
  if (v.estado === 'SIN_FECHA' || v.diasRestantes === null) return null;
  const d = Math.abs(v.diasRestantes);
  const dias = `${d} ${d === 1 ? 'día' : 'días'}`;
  if (v.estado === 'VENCIDA') return d === 0 ? 'Venció hoy' : `Venció hace ${dias}`;
  return v.diasRestantes === 0 ? 'Vence hoy' : `Vence en ${dias}`;
}

/** Las pastillas de una empresa en prueba, en el orden en que se leen. Una empresa común no tiene. */
export function pastillasDeLaDemo(
  demo: DemoDeLaEmpresa | null | undefined,
  misiones?: { hechas: number; total: number } | null
): PastillaDeLaDemo[] {
  if (!demo) return [];
  // Una demo archivada no cuenta como prueba en curso: ni «Lista para la venta», ni «Sin ingresos», ni
  // «Rubro sin tiendas», ni las misiones. La pantalla lee `archivada` tal cual la manda el servidor.
  if (demo.archivada) return [];
  const pastillas: PastillaDeLaDemo[] = [];
  // Demo que vence (C): lo primero que se lee, porque es lo único con fecha.
  const plazo = demo.vencimiento;
  if (plazo?.estado === 'VENCIDA') {
    pastillas.push({
      clave: 'vencida',
      texto: textoDelPlazo(plazo) ?? 'Venció',
      variante: 'destructive',
      ayuda:
        'La prueba de 15 días venció: la empresa quedó desactivada y nadie entra. No se borró nada. Extendé la prueba o pasala al plan completo desde su ficha.',
    });
  } else if (plazo?.estado === 'POR_VENCER') {
    pastillas.push({
      clave: 'por-vencer',
      texto: textoDelPlazo(plazo) ?? 'Por vencer',
      variante: 'warning',
      ayuda:
        'La prueba de 15 días vence pronto. Al vencer, la empresa se desactiva (sin borrar nada). Podés extenderla desde su ficha.',
    });
  }
  if (demo.listaParaLaVenta) {
    pastillas.push({
      clave: 'lista',
      texto: TEXTO_LISTA_PARA_LA_VENTA,
      variante: 'success',
      ayuda:
        'Eligió su tienda, cargó 50 productos propios o más y vinculó al menos uno con esa tienda: es buen momento para contactarla.',
    });
  }
  if (misiones) {
    pastillas.push({
      clave: 'misiones',
      texto: textoDeMisiones(misiones.hechas, misiones.total),
      variante: 'secondary',
      ayuda: 'Las misiones de la prueba que ya hizo.',
    });
  }
  if (demo.productos.enElTope) {
    pastillas.push({
      clave: 'en-el-tope',
      texto: TEXTO_EN_EL_TOPE,
      variante: 'info',
      ayuda:
        'Llegó al tope de productos propios de la prueba: no puede cargar más sin el plan completo.',
    });
  }
  if (demo.abandonada) {
    pastillas.push({
      clave: 'sin-ingresos',
      texto: textoSinIngresos(demo.diasSinIngreso),
      variante: 'warning',
      ayuda:
        'Nadie de la empresa ingresó en más de 60 días. Que no entre nadie no pausa la prueba: decidí vos qué hacer.',
    });
  }
  if (demo.ejeEstado === 'SIN_TIENDAS_LEIDAS') {
    pastillas.push({
      clave: 'rubro-sin-tiendas',
      texto: TEXTO_RUBRO_SIN_TIENDAS,
      variante: 'warning',
      ayuda:
        'Su rubro ya no tiene ninguna tienda leída. Pasala a otro rubro con «Cambiar rubro y tienda», o leé una tienda de ese rubro.',
    });
  }
  return pastillas;
}

/** El selector de demos de la lista de empresas. `todas` no filtra. */
export const OPCIONES_DE_FILTRO_DE_DEMO: Array<{ valor: 'todas' | FiltroDeDemo; texto: string }> = [
  { valor: 'todas', texto: 'Todas las empresas' },
  { valor: 'por-vencer', texto: 'Prueba por vencer' },
  { valor: 'vencida', texto: 'Prueba vencida' },
  { valor: 'lista-para-la-venta', texto: TEXTO_LISTA_PARA_LA_VENTA },
  { valor: 'abandonada', texto: 'Sin ingresos hace tiempo' },
  { valor: 'rubro-sin-tiendas', texto: TEXTO_RUBRO_SIN_TIENDAS },
];

export const TEXTOS_DEL_CAMBIO = {
  titulo: 'Cambiar rubro y tienda',
  boton: 'Cambiar rubro y tienda',
  ayuda: (empresa: string) =>
    `${empresa} compara contra una sola tienda. Si cambiás la tienda, la actual queda guardada con sus ejemplos y con los vínculos de sus productos, y se cargan ejemplos de la nueva. No se borra nada.`,
  rubro: 'Rubro',
  tienda: 'Tienda',
  dejarLaActual: (nombre: string) => `Dejar la que usa ahora (${nombre})`,
  deEsteRubro: 'De este rubro',
  otras: 'Otras tiendas',
  sugerida: 'Sugerida',
  ejemplos: (n: number) => `${n} ejemplos`,
  cambiar: 'Cambiar',
  cancelar: 'Cancelar',
  trabajando: 'Cambiando…',
  sinOpciones: 'Todavía no hay rubros con tiendas leídas para elegir.',
  rubroSinTiendas: (rubro: string) =>
    `El rubro actual (${rubro}) ya no tiene ninguna tienda leída: elegí otro rubro.`,
  siembraPendiente:
    'Los ejemplos de la tienda nueva todavía no se cargaron: se completan solos cuando alguien de la empresa abre sesión.',
  noSeLeyo: 'No se pudieron leer las opciones de esta empresa.',
  noSePudo: 'No se pudo cambiar.',
  hecho: (empresa: string, rubro: string, tienda: string) =>
    `${empresa} quedó en ${rubro}, con la tienda ${tienda}.`,
  sinCambios: (empresa: string) => `${empresa} ya estaba en ese rubro y con esa tienda.`,
  ejemplosCargados: (archivados: number, sembrados: number, desarchivados: number) => {
    const partes: string[] = [];
    const ej = (n: number) => (n === 1 ? '1 ejemplo' : `${n} ejemplos`);
    if (archivados > 0) partes.push(`${ej(archivados)} guardado${archivados === 1 ? '' : 's'}`);
    if (desarchivados > 0) partes.push(`${ej(desarchivados)} de vuelta`);
    if (sembrados > 0) partes.push(`${ej(sembrados)} nuevo${sembrados === 1 ? '' : 's'}`);
    return partes.length > 0 ? `${partes.join(', ')}.` : undefined;
  },
} as const;

export const TEXTOS_DE_LA_FICHA = {
  titulo: 'Rubro y tienda de la prueba',
  descripcion:
    'Con qué tienda se compara esta empresa mientras prueba Prixia. Es una sola; las que tuvo antes quedan guardadas.',
  rubro: 'Rubro',
  tienda: 'Tienda',
  guardadas: (n: number) => (n === 1 ? '1 tienda guardada' : `${n} tiendas guardadas`),
  sinTienda: 'Todavía no eligió una',
  sinRubro: 'Sin rubro',
  /** Una archivada no se toca: el servidor responde 409; la pantalla ni ofrece el cambio. */
  archivada: 'Está archivada: restaurala para cambiarle el rubro o la tienda.',
  productosTitulo: 'Productos',
  productos: (usados: number, tope: number | null) =>
    tope === null ? `${usados} productos propios` : `${usados} de ${tope} productos propios`,
} as const;

/* Convertir la demo en empresa con plan completo (la ficha de la empresa, sólo si es una prueba). */
export const TEXTOS_DE_LA_CONVERSION = {
  boton: 'Pasar al plan completo',
  titulo: (empresa: string) => `¿Pasar ${empresa} al plan completo?`,
  intro: (empresa: string) =>
    `${empresa} deja de ser una prueba y sigue con el plan que elijas acá, con sus módulos.`,
  plan: 'Plan con el que sigue',
  hasta: 'Hasta',
  faltaPlan: 'Elegí el plan y hasta cuándo.',
  quePasa: [
    'La prueba termina: rige el plan que elegiste, con sus módulos y su tope de personas. Si estaba desactivada porque la prueba venció, vuelve a entrar.',
    'Los productos de ejemplo no se borran: quedan marcados «De ejemplo» y la empresa los archiva cuando quiera.',
    'Las tiendas que tenía guardadas vuelven a estar activas, con los vínculos de sus productos.',
    'Los productos de ejemplo siguen, pero sin sus vínculos: nadie los confirmó.',
    'Las estrategias automáticas pasan a supervisadas: nada mueve precios sin que una persona lo apruebe.',
  ],
  confirmar: 'Sí, pasar al plan completo',
  cancelar: 'Cancelar',
  trabajando: 'Pasando…',
  noSePudo: 'No se pudo pasar al plan completo.',
  hecho: (empresa: string) => `${empresa} pasó al plan completo.`,
  conPlan: (plan: string) => `Sigue con el plan ${plan}.`,
  sinEstrategias: 'No tenía estrategias automáticas que pasar a supervisadas.',
  estrategias: (nombres: string[]) =>
    nombres.length === 1
      ? `1 estrategia automática pasó a supervisada: ${nombres[0]}.`
      : `${nombres.length} estrategias automáticas pasaron a supervisadas: ${nombres.join(', ')}.`,
} as const;

/* Demo que vence (C, 2-nov): el plazo de la prueba en la ficha y «Extender prueba». */
export const TEXTOS_DEL_PLAZO = {
  titulo: 'Prueba',
  vence: (fecha: string) => `Vence el ${fecha}`,
  vencio: (fecha: string) => `Venció el ${fecha}: la empresa quedó desactivada (no se borró nada).`,
  sinFecha: 'Sin fecha de vencimiento',
  boton: 'Extender prueba',
  dialogoTitulo: (empresa: string) => `Extender la prueba de ${empresa}`,
  dialogoAyuda:
    'Se suman días desde la fecha en que vence o, si ya venció, desde hoy. Si estaba desactivada porque venció, vuelve a entrar.',
  dias: 'Días más',
  confirmar: 'Extender',
  cancelar: 'Cancelar',
  trabajando: 'Extendiendo…',
  hecho: (empresa: string, fecha: string) => `La prueba de ${empresa} vence ahora el ${fecha}.`,
  reactivada: 'La empresa vuelve a entrar.',
  noSePudo: 'No se pudo extender la prueba.',
} as const;
